import axios from 'axios';
import { Contact } from '../types/Contact';
import authService from './AuthService';
import { getApiUrl } from '../utils/env';

const API_BASE_URL = getApiUrl();

// Log API-Konfiguration beim Laden des Services
console.log('🔧 ContactService Konfiguration:');
console.log('   API_BASE_URL (verwendet):', API_BASE_URL);
console.log('   Runtime Config verfügbar:', !!window.__ENV__);
console.log('   process.env.REACT_APP_API_URL:', process.env.REACT_APP_API_URL || 'nicht gesetzt');
console.log('   window.__ENV__.REACT_APP_API_URL:', window.__ENV__?.REACT_APP_API_URL || 'nicht gesetzt');
if (!process.env.REACT_APP_API_URL && !window.__ENV__?.REACT_APP_API_URL) {
  console.warn('⚠️  Keine API URL konfiguriert - verwende Default');
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor für Authentication
api.interceptors.request.use(
  (config) => {
    console.log('🌐 API Request:', config.method?.toUpperCase(), `${API_BASE_URL}${config.url}`);
    const token = authService.getAccessToken();
    console.log('🔍 Token check:', token ? `Token available (${token.substring(0, 50)}...)` : 'No token found');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔑 Authorization header set:', `Bearer ${token.substring(0, 50)}...`);
    } else {
      console.log('❌ Kein Token verfügbar - API Call wird ohne Authentication gemacht');
    }
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor für 401 Errors
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.status, response.config.method?.toUpperCase(), response.config.url);
    return response;
  },
  async (error) => {
    console.error('❌ API Error:', error.response?.status, error.config?.method?.toUpperCase(), error.config?.url, error.message);
    if (error.response?.status === 401) {
      // Token abgelaufen, zur Anmeldung weiterleiten
      await authService.login();
    }
    return Promise.reject(error);
  }
);

export class ContactService {
  static async getAllContacts(forceUserView: boolean = false, aktivFilter: string = 'aktiv', nummernFilter: string = 'alle_nummern'): Promise<Contact[]> {
    let url = '/contacts';
    const params = new URLSearchParams();
    
    if (forceUserView) {
      params.append('forceUserView', 'true');
    }
    
    if (aktivFilter && aktivFilter !== 'alle') {
      params.append('aktiv', aktivFilter);
    }
    
    if (nummernFilter && nummernFilter !== 'alle_nummern') {
      params.append('nummern', nummernFilter);
    }
    
    if (params.toString()) {
      url += '?' + params.toString();
    }
    
    const response = await api.get<Contact[]>(url);
    return response.data;
  }

  static async createContact(contact: Partial<Contact>): Promise<Contact> {
    // Validierung: Entweder Nachname ODER Firmenname muss vorhanden sein (Vorname ist optional)
    const hasLastName = contact.lastname && contact.lastname.trim();
    const hasCompanyName = contact.companyname && contact.companyname.trim();
    
    if (!hasLastName && !hasCompanyName) {
      throw new Error('Entweder Nachname oder Firmenname ist erforderlich');
    }
    
    const response = await api.post<Contact>('/contacts', contact);
    return response.data;
  }

  static async updateContact(id: string, contact: Partial<Contact>): Promise<void> {
    await api.put(`/contacts/${id}`, contact);
  }

  static async deleteContact(id: string): Promise<void> {
    await api.delete(`/contacts/${id}`);
  }

  static async deleteAllContacts(): Promise<{ message: string; deletedCount: number; totalCount: number }> {
    const response = await api.delete<{ message: string; deletedCount: number; totalCount: number }>('/contacts/all');
    return response.data;
  }

  static async checkAllPhoneNumbers(): Promise<{ 
    message: string; 
    results: { 
      total: number; 
      checked: number; 
      updated: number; 
      validNumbers: number; 
      invalidNumbers: number; 
      errors: string[] 
    } 
  }> {
    const response = await api.post<{ 
      message: string; 
      results: { 
        total: number; 
        checked: number; 
        updated: number; 
        validNumbers: number; 
        invalidNumbers: number; 
        errors: string[] 
      } 
    }>('/contacts/check-all-phone-numbers');
    return response.data;
  }

  static async searchContacts(query: string): Promise<Contact[]> {
    const response = await api.get<Contact[]>(`/contacts/search/${encodeURIComponent(query)}`);
    return response.data;
  }

  static async getUserInfo(): Promise<any> {
    const response = await api.get('/user');
    return response.data;
  }
}

export default ContactService;
